import { useCallback } from 'react';
import type { ElasticListVehicle } from '@//app/api/search/vehicles';
import type { Market } from '@//config/domain-locale-map';
import { useLegacyTracking } from '@//providers/LegacyTrackingProvider';
import { useMarket } from '@//providers/MarketSettingsProvider';
import type { LegacyTrackExposureEventData } from './legacy-types';
import { mapElasticListVehicleToLegacyItem } from './utils';

export const createLegacyTrackView = (
  market: Market,
  vehicle: ElasticListVehicle,
  options: useLegacyTrackExposureOptions,
): LegacyTrackExposureEventData => {
  const eventData: LegacyTrackExposureEventData = {
    eventType: 'exposure',
    item: mapElasticListVehicleToLegacyItem(market, vehicle),
    list: 'SOK',
    urlParameters: options.parameters
      ? options.parameters[0] === '?'
        ? options.parameters.slice(1)
        : options.parameters
      : undefined,
    information: {
      index: options.index,
    },
  };

  return eventData;
};

interface useLegacyTrackExposureOptions {
  index: number;
  parameters?: string;
}

export const useLegacyTrackExposure = () => {
  const market = useMarket();
  const { track } = useLegacyTracking();

  const trackLegacyExposure = useCallback(
    (vehicle: ElasticListVehicle, options: useLegacyTrackExposureOptions) => {
      // Track event with only the event-specific data
      // The provider will inject base data (source, url, eventTimestamp, userData)
      track({
        event: 'exposure',
        data: createLegacyTrackView(market, vehicle, options),
      });
    },
    [market, track],
  );

  return {
    trackLegacyExposure,
  };
};
